/**
 * OutlookSocketListener
 *
 * Mounts once inside OutlookNew. No DOM output.
 * Listens for socket.io events emitted by the backend after a Graph webhook fires:
 *
 *   "new_message"     — a new email arrived (Graph changeType: created)
 *   "message_updated" — an email changed (read, flag, move … Graph changeType: updated)
 *
 * On new_message:
 *   - Deduplicates via localStorage-backed seenMessageIds set
 *   - Upserts the message into mailMessages Redux
 *   - Upserts / updates the thread in mailThreads Redux
 *   - Increments the folder unread badge in mailFolders Redux
 *   - Detects new mail vs reply (thread already in Redux → reply)
 *   - Shows an in-app toast notification
 *   - Adds entry to bell-panel notification history
 *   - Shows a browser desktop notification when tab is backgrounded
 *     (browser notification onclick navigates to the thread)
 *
 * On message_updated:
 *   - Upserts the updated message
 *   - Syncs thread hasUnread / unreadCount
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socket from '../Sales1/socket';
import { SERVER_URL } from '../../../databucket';
import { upsertMessage } from '../../../Slices/mailMessagesSlice';
import { upsertThread, markThreadUnread } from '../../../Slices/mailThreadsSlice';
import { incrementUnread } from '../../../Slices/mailFoldersSlice';
import {
  pushNotification,
  addMailNotification,
  setSelectedThread,
} from '../../../Slices/mailUiSlice';
import { selectFolderAndLoad } from '../../../Slices/Thunk/graphMailThunks';

// ---------------------------------------------------------------------------
// Polling interval (ms) — used when running on localhost where Graph webhooks
// cannot deliver events to a non-public URL.
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Deduplication — module-level singleton so it survives component re-renders
// ---------------------------------------------------------------------------

const SEEN_KEY = 'bpa_seen_msg_ids';
const MAX_SEEN = 500;

function loadSeenIds() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
}

function saveSeenIds(set) {
  try {
    // Only keep the last MAX_SEEN to prevent unbounded growth
    const arr = [...set].slice(-MAX_SEEN);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
}

const seenMessageIds = loadSeenIds();

// ---------------------------------------------------------------------------
// Build a thread summary from a single incoming message.
// If the thread already exists in Redux, we merge rather than replace.
// ---------------------------------------------------------------------------
function buildThreadFromMessage(msg, existingThread) {
  const base = existingThread || {};
  const isUnread = !msg.isRead && !msg.isDraft;

  return {
    conversationId:   msg.conversationId,
    folderId:         msg.folderId || base.folderId,
    subject:          msg.subject  || base.subject || '(No subject)',
    bodyPreview:      msg.bodyPreview || base.bodyPreview || '',
    latestReceivedAt: msg.receivedDateTime || msg.sentDateTime || base.latestReceivedAt,
    latestSenderId:   msg.from?.email || base.latestSenderId,
    latestSenderName: msg.from?.name  || msg.from?.email || base.latestSenderName,
    participants:     base.participants
      ? [...new Set([...(base.participants), msg.from?.name || msg.from?.email].filter(Boolean))]
      : [msg.from?.name || msg.from?.email].filter(Boolean),
    hasUnread:   isUnread ? true  : (base.hasUnread  ?? false),
    unreadCount: isUnread ? (base.unreadCount || 0) + 1 : (base.unreadCount || 0),
    messageCount: (base.messageCount || 0) + (existingThread ? 0 : 1),
    messageIds:   base.messageIds
      ? (base.messageIds.includes(msg.id) ? base.messageIds : [...base.messageIds, msg.id])
      : [msg.id],
    isFlagged:      base.isFlagged      || false,
    hasAttachments: base.hasAttachments || msg.hasAttachments || false,
    categories:     base.categories     || msg.categories     || [],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OutlookSocketListener({ userEmail }) {
  const dispatch    = useDispatch();
  const threadsById = useSelector(s => s.mailThreads.byId);
  const foldersById = useSelector(s => s.mailFolders.byId);

  // Used by the polling fallback — tracks the timestamp of the last poll
  const lastPollTimeRef = useRef(new Date().toISOString());

  // Determine whether we're running locally (webhooks won't reach us)
  const isLocalhost = SERVER_URL.includes('localhost') || SERVER_URL.includes('127.0.0.1');

  // Stable ref so the poll callback always has the latest Redux state
  const threadsByIdRef = useRef(threadsById);
  const foldersByIdRef = useRef(foldersById);
  useEffect(() => { threadsByIdRef.current = threadsById; }, [threadsById]);
  useEffect(() => { foldersByIdRef.current = foldersById; }, [foldersById]);

  useEffect(() => {
    if (!userEmail) return;

    // ── new_message ──────────────────────────────────────────────────────────
    function handleNewMessage(msg) {
      if (!msg?.id || !msg?.conversationId) return;

      // --- Deduplication ---
      if (seenMessageIds.has(msg.id)) return;
      seenMessageIds.add(msg.id);
      saveSeenIds(seenMessageIds);

      // Only process messages addressed to (or sent from) the current user
      const isForMe =
        msg.toRecipients?.some(r => r.email?.toLowerCase() === userEmail.toLowerCase()) ||
        msg.ccRecipients?.some(r => r.email?.toLowerCase() === userEmail.toLowerCase()) ||
        msg.from?.email?.toLowerCase() === userEmail.toLowerCase();
      if (!isForMe) return;

      // --- Reply detection: thread already existed before this message? ---
      const existingThread = threadsById[msg.conversationId];
      const isReply = !!existingThread;

      // 1. Upsert message
      dispatch(upsertMessage(msg));

      // 2. Upsert / update thread
      const thread = buildThreadFromMessage(msg, existingThread);
      dispatch(upsertThread(thread));

      // 3. Increment folder unread badge (only if message is unread)
      if (!msg.isRead && !msg.isDraft && msg.folderId && foldersById[msg.folderId]) {
        dispatch(incrementUnread({ folderId: msg.folderId }));
      }

      // 4. Build notification strings
      const sender  = msg.from?.name || msg.from?.email || 'Unknown';
      const subject = msg.subject || '(No subject)';
      const notifType = isReply ? 'reply' : 'new_mail';
      const toastMsg  = isReply
        ? `${sender} replied: ${subject}`
        : `New email from ${sender}: ${subject}`;

      // 5. In-app toast
      dispatch(pushNotification({
        type:          'info',
        message:       toastMsg,
        autoDismissMs: 6000,
      }));

      // 6. Bell-panel history entry
      dispatch(addMailNotification({
        type:           notifType,
        sender,
        subject,
        conversationId: msg.conversationId,
        folderId:       msg.folderId || existingThread?.folderId || null,
      }));

      // 7. Browser desktop notification — only when tab is not visible
      const isBackground = document.visibilityState !== 'visible';
      if (isBackground && Notification.permission === 'granted') {
        try {
          const notif = new Notification(sender, {
            body: subject,
            icon: '/favicon.ico',
            tag:  msg.id,
          });
          // Click-to-open: focus the tab and navigate to the thread
          notif.onclick = () => {
            window.focus();
            const targetFolderId = msg.folderId || existingThread?.folderId;
            if (targetFolderId) {
              dispatch(selectFolderAndLoad({ folderId: targetFolderId }));
            }
            dispatch(setSelectedThread({ conversationId: msg.conversationId }));
          };
        } catch { /* ignore */ }
      }
    }

    // ── message_updated ───────────────────────────────────────────────────────
    function handleMessageUpdated(msg) {
      if (!msg?.id || !msg?.conversationId) return;

      // Only care about messages in the current user's mailbox
      const isRelevant =
        msg.toRecipients?.some(r => r.email?.toLowerCase() === userEmail.toLowerCase()) ||
        msg.ccRecipients?.some(r => r.email?.toLowerCase() === userEmail.toLowerCase()) ||
        msg.from?.email?.toLowerCase() === userEmail.toLowerCase();
      if (!isRelevant) return;

      dispatch(upsertMessage(msg));

      // Sync thread unread state if the message read-status changed
      const existing = threadsById[msg.conversationId];
      if (existing && !msg.isRead) {
        dispatch(markThreadUnread({ conversationId: msg.conversationId }));
      }
    }

    socket.on('new_message',     handleNewMessage);
    socket.on('message_updated', handleMessageUpdated);

    // Request desktop notification permission once (prompts user on first visit)
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    // ── Polling fallback (localhost only) ─────────────────────────────────────
    // Graph webhooks can't reach localhost, so we poll the backend every 30s.
    let pollTimer = null;
    if (isLocalhost) {
      async function pollInbox() {
        const since = lastPollTimeRef.current;
        // Advance the cursor immediately so rapid polls don't double-fire
        lastPollTimeRef.current = new Date().toISOString();
        try {
          const url = `${SERVER_URL}/api/mail/inbox-poll?since=${encodeURIComponent(since)}`;
          const resp = await fetch(url, {
            headers: { 'x-user-email': userEmail },
          });
          if (!resp.ok) return;
          const { messages = [] } = await resp.json();
          messages.forEach(handleNewMessage);
        } catch { /* network error — skip this tick */ }
      }
      pollTimer = setInterval(pollInbox, POLL_INTERVAL_MS);
    }

    return () => {
      socket.off('new_message',     handleNewMessage);
      socket.off('message_updated', handleMessageUpdated);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [userEmail, dispatch, isLocalhost]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
